/**
 * PipelineOutputNode — Custom ReactFlow node for the pipeline output.
 *
 * Selection-driven: only rendered when the user has explicitly curated a
 * set of output tables / outputs via the PipelineOutputsDrawer (persisted
 * to workspace_meta.selected_output_refs). Represents "the things I want
 * to ship" — distinct from the raw `__mapped_*__` views and post-map
 * transform outputs that may exist on the canvas.
 *
 * Visual spec:
 * - Width 240px, white background, teal left border
 * - Header: output icon + "Pipeline Output" + selection-count badge
 * - Description: "{N} selected output(s)"
 * - Validation/quality badges (when checks have run)
 * - Action buttons: [Review & Export] [Export…]
 * - Top Handle (from selection), Bottom Handle (to destination)
 */
import React from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Box, Typography, alpha, Button, Chip } from '@mui/material';
import {
  FileDownloadOutlined as DownloadIcon,
  Output as OutputIcon,
  OpenInNew as ExportIcon,
  ViewList as ViewListIcon,
  AddCircleOutline as AddTransformIcon,
  CheckCircleOutline as PassIcon,
  WarningAmber as WarnIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';

export interface PipelineOutputNodeData {
  title: string;
  description?: string;
  status: string;
  rowCount?: number;
  columnCount?: number;
  /** Number of completed output tables across all transforms. */
  outputCount?: number;
  /** Callback to open the outputs drawer listing all tables. */
  onViewAllOutputs?: () => void;
  /** Callback to open the export panel / connector picker (quick-export for last table). */
  onExport?: () => void;
  /** Validation status badge. */
  validationStatus?: 'pass' | 'warn' | 'error';
  /** Number of warnings or errors from validation. */
  validationCount?: number;
  /** Number of failed mapping views. */
  failedViewCount?: number;
  /** Callback to add a post-mapping transform. */
  onAddTransform?: () => void;
  /** Quality gate status from review aggregation. */
  qualityStatus?: 'pass' | 'warn' | 'fail' | 'no_review';
  /** Number of quality issues (failed checks or warnings). */
  qualityIssueCount?: number;
}

const PipelineOutputNode: React.FC<NodeProps<PipelineOutputNodeData>> = ({
  data,
  selected,
  sourcePosition,
  targetPosition,
}) => {
  const targetHandlePos = targetPosition ?? Position.Top;
  const sourceHandlePos = sourcePosition ?? Position.Bottom;
  const isPending = data.status === 'pending';
  // Selection-driven node — single accent (teal) since it always represents
  // a curated export set, regardless of whether any contained refs are
  // pre-mapping transforms, target tables, or post-mapping outputs.
  const accentColor = '#0d9488';
  const outputCount = data.outputCount ?? 0;

  return (
    <Box
      sx={{
        width: 240,
        background: '#fff',
        borderRadius: 0.5,
        border: selected
          ? `2px solid ${accentColor}`
          : isPending
            ? '1.5px dashed #d1d5db'
            : `1.5px solid ${alpha(accentColor, 0.3)}`,
        borderLeft: `4px solid ${isPending ? '#9ca3af' : accentColor}`,
        boxShadow: selected
          ? `0 0 0 3px ${alpha(accentColor, 0.15)}`
          : '0 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        cursor: 'pointer',
        opacity: isPending ? 0.7 : 1,
      }}
    >
      {/* Target handle */}
      <Handle
        type="target"
        position={targetHandlePos}
        style={{
          background: isPending ? '#9ca3af' : accentColor,
          width: 8,
          height: 8,
          border: `2px solid ${alpha(isPending ? '#9ca3af' : accentColor, 0.3)}`,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: alpha(isPending ? '#9ca3af' : accentColor, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <OutputIcon sx={{ fontSize: 16, color: isPending ? '#9ca3af' : accentColor }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{
              fontSize: 12,
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Pipeline Output
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: 10, color: '#6b7280', letterSpacing: 0.3 }}
          >
            {data.description || (
              outputCount > 0
                ? `${outputCount} output table${outputCount !== 1 ? 's' : ''} ready`
                : isPending
                  ? 'Awaiting transforms'
                  : 'CLEAN DATA READY'
            )}
          </Typography>
        </Box>
        {/* Count badge */}
        {outputCount > 0 && (
          <Box
            sx={{
              minWidth: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: accentColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              px: 0.5,
            }}
          >
            <Typography sx={{ fontSize: 11, color: '#fff', fontWeight: 700, lineHeight: 1 }}>
              {outputCount}
            </Typography>
          </Box>
        )}
        {outputCount === 0 && !isPending && (
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: accentColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <DownloadIcon sx={{ fontSize: 13, color: '#fff' }} />
          </Box>
        )}
      </Box>

      {/* Validation + quality + failed view badges */}
      {(data.validationStatus || data.qualityStatus || (data.failedViewCount != null && data.failedViewCount > 0)) && (
        <Box sx={{ px: 1.5, pb: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {data.validationStatus === 'pass' && (
            <Chip
              icon={<PassIcon sx={{ fontSize: 12 }} />}
              label="Validated"
              size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: alpha('#059669', 0.1), color: '#059669' }}
            />
          )}
          {data.validationStatus === 'warn' && (
            <Chip
              icon={<WarnIcon sx={{ fontSize: 12 }} />}
              label={`${data.validationCount ?? 0} warning${(data.validationCount ?? 0) !== 1 ? 's' : ''}`}
              size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: alpha('#d97706', 0.1), color: '#d97706' }}
            />
          )}
          {data.validationStatus === 'error' && (
            <Chip
              icon={<ErrorIcon sx={{ fontSize: 12 }} />}
              label={`${data.validationCount ?? 0} error${(data.validationCount ?? 0) !== 1 ? 's' : ''}`}
              size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: alpha('#dc2626', 0.1), color: '#dc2626' }}
            />
          )}
          {data.qualityStatus === 'fail' && (
            <Chip
              icon={<ErrorIcon sx={{ fontSize: 12 }} />}
              label={`${data.qualityIssueCount ?? 0} quality issue${(data.qualityIssueCount ?? 0) !== 1 ? 's' : ''}`}
              size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: alpha('#dc2626', 0.1), color: '#dc2626' }}
            />
          )}
          {data.qualityStatus === 'warn' && (
            <Chip
              icon={<WarnIcon sx={{ fontSize: 12 }} />}
              label={`${data.qualityIssueCount ?? 0} quality warning${(data.qualityIssueCount ?? 0) !== 1 ? 's' : ''}`}
              size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: alpha('#d97706', 0.1), color: '#d97706' }}
            />
          )}
          {data.qualityStatus === 'pass' && (
            <Chip
              icon={<PassIcon sx={{ fontSize: 12 }} />}
              label="Quality ✓"
              size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: alpha('#059669', 0.1), color: '#059669' }}
            />
          )}
          {data.qualityStatus === 'no_review' && (
            <Chip
              label="No review"
              size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: alpha('#64748b', 0.1), color: '#64748b' }}
            />
          )}
          {data.failedViewCount != null && data.failedViewCount > 0 && (
            <Chip
              icon={<WarnIcon sx={{ fontSize: 12 }} />}
              label={`${data.failedViewCount} failed`}
              size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: alpha('#d97706', 0.1), color: '#d97706' }}
            />
          )}
        </Box>
      )}

      {/* Stats */}
      <Box sx={{ px: 1.5, pb: 0.5 }}>
        {data.rowCount != null && (
          <Typography
            variant="caption"
            sx={{ fontSize: 11, color: '#64748b', display: 'block', lineHeight: 1.3 }}
          >
            {data.rowCount.toLocaleString()} rows
            {data.columnCount != null && ` · ${data.columnCount} columns`}
          </Typography>
        )}
      </Box>

      {/* Action buttons */}
      {!isPending && (
        <Box sx={{ px: 1.5, pb: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<ViewListIcon sx={{ fontSize: 12 }} />}
            onClick={(e) => {
              e.stopPropagation();
              data.onViewAllOutputs?.();
            }}
            sx={{
              fontSize: 10,
              textTransform: 'none',
              bgcolor: accentColor,
              '&:hover': { bgcolor: '#0f766e' },
              borderRadius: 1,
              px: 1,
              py: 0.25,
              minHeight: 0,
              lineHeight: 1.4,
              boxShadow: 'none',
            }}
          >
            Review & Export
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ExportIcon sx={{ fontSize: 12 }} />}
            onClick={(e) => {
              e.stopPropagation();
              data.onExport?.();
            }}
            sx={{
              fontSize: 10,
              textTransform: 'none',
              color: accentColor,
              borderColor: alpha(accentColor, 0.3),
              '&:hover': { borderColor: accentColor, bgcolor: alpha(accentColor, 0.04) },
              borderRadius: 1,
              px: 1,
              py: 0.25,
              minHeight: 0,
              lineHeight: 1.4,
            }}
          >
            Export…
          </Button>
          {data.onAddTransform && (
            <Button
              size="small"
              variant="text"
              startIcon={<AddTransformIcon sx={{ fontSize: 12 }} />}
              onClick={(e) => {
                e.stopPropagation();
                data.onAddTransform?.();
              }}
              sx={{
                fontSize: 10,
                textTransform: 'none',
                color: '#64748b',
                '&:hover': { color: accentColor, bgcolor: alpha(accentColor, 0.04) },
                borderRadius: 1,
                px: 0.75,
                py: 0.25,
                minHeight: 0,
                lineHeight: 1.4,
              }}
            >
              Transform
            </Button>
          )}
        </Box>
      )}

      {/* Source handle — for continuation */}
      <Handle
        type="source"
        position={sourceHandlePos}
        style={{
          background: isPending ? '#9ca3af' : accentColor,
          width: 8,
          height: 8,
          border: `2px solid ${alpha(isPending ? '#9ca3af' : accentColor, 0.3)}`,
        }}
      />
    </Box>
  );
};

export default PipelineOutputNode;
